import type { ReactNode } from "react";

import { lazy, Suspense, useMemo } from "react";
import { Redirect, Route, Switch, useParams } from "react-router-dom";

import { Spin } from "antd";

import { ErrorBoundary } from "../../common/error/errorBoundary";
import { AUTH_CALLBACK_PATH } from "../auth/authRoutes";
import { useUserContext } from "../context/User.context";

const AuditWorkspace = lazy(() =>
  import("../audit/auditWorkspace").then((module) => ({
    default: module.AuditWorkspace,
  })),
);
const AuditsPage = lazy(() =>
  import("../audits/auditsPage").then((module) => ({
    default: module.AuditsPage,
  })),
);
const Dashboard = lazy(() =>
  import("../dashboard/dashboard").then((module) => ({
    default: module.Dashboard,
  })),
);
const Queue = lazy(() =>
  import("../queues/queue").then((module) => ({ default: module.Queue })),
);
const QueuesPage = lazy(() =>
  import("../queues/queuesPage").then((module) => ({
    default: module.QueuesPage,
  })),
);
const NewQueuesPage = lazy(() =>
  import("../queues/newQueuesPage").then((module) => ({
    default: module.NewQueuesPage,
  })),
);
const Reports = lazy(() =>
  import("../reports/reports").then((module) => ({ default: module.Reports })),
);
const Claims = lazy(() =>
  import("../claims/claims").then((module) => ({
    default: module.Claims,
  })),
);
const Transfers = lazy(() =>
  import("../transfers").then((module) => ({ default: module.default })),
);
const Profile = lazy(() =>
  import("../user/profile").then((module) => ({ default: module.Profile })),
);
const Users = lazy(() =>
  import("../user/users").then((module) => ({ default: module.Users })),
);
const Colors = lazy(() =>
  import("./design/colors").then((module) => ({ default: module.Colors })),
);
const Playground = lazy(() =>
  import("../../common/playground/playground").then((module) => ({
    default: module.Playground,
  })),
);

const RolesPage = lazy(() =>
  import("../admin/settings/roles/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);

const AdminSettings = lazy(() =>
  import("../admin/settings/layout").then((module) => ({
    default: module.default,
  })),
);

const SsoIdentityProvidersPage = lazy(() =>
  import("../admin/settings/ssoIdentityProviders/SsoIdentityProvidersPage").then(
    (module) => ({
      default: module.SsoIdentityProvidersPage,
    }),
  ),
);

const AutodorRulesPage = lazy(() =>
  import("../autodorRules/AutodorRulesPage").then((module) => ({
    default: module.AutodorRulesPage,
  })),
);
const AuthCallback = lazy(() =>
  import("../auth/AuthCallback").then((module) => ({
    default: module.AuthCallback,
  })),
);
const LegacySearchRedirect = () => {
  const { searchFilter } = useParams<{ searchFilter?: string }>();

  if (!searchFilter) {
    return <Redirect to="/claims" />;
  }

  let decodedSearchFilter = searchFilter;
  try {
    decodedSearchFilter = decodeURIComponent(searchFilter);
  } catch {
    decodedSearchFilter = searchFilter;
  }

  return <Redirect to={`/claims?filter=${encodeURI(decodedSearchFilter)}`} />;
};

const RouteWrapper = ({
  boundaryDescription,
  children,
}: {
  boundaryDescription: string;
  children: ReactNode;
}) => (
  <div
    data-id="routes-wrapper"
    className="af-flex af-h-full af-min-h-full af-w-full af-flex-col"
  >
    <ErrorBoundary boundaryDescription={boundaryDescription}>
      <Suspense fallback={<Spin />}>{children}</Suspense>
    </ErrorBoundary>
  </div>
);

/**
 * The SSO callback renders outside `Routes` because it runs before a session
 * exists, so there is no `UserContext` to read permissions from.
 */
const AuthCallbackRoute = () => (
  <RouteWrapper boundaryDescription={`Route [${AUTH_CALLBACK_PATH}]`}>
    <AuthCallback />
  </RouteWrapper>
);

const Routes = () => {
  const { permissions } = useUserContext();

  // add dev routes if they exist
  // TODO: lazy load dev routes to split bundle or restructure build so that dev routes are not included in prod build
  const devRoutes = useMemo(() => {
    const dr = (process.env.REACT_APP_DEV_ROUTES ?? "")?.split(",");
    dr.length > 0 && console.log("Dev routes enabled", dr);
    return dr;
  }, []);
  const permittedRoutes = permissions.routes.concat(devRoutes);

  // assign the logged in user's allowed routes
  const routes = {
    "/users": <Users />,
    // '/auth/login': <Login />,
    "/user/profile": <Profile />,
    "/workspace/:batchClaimId": <AuditWorkspace />,
    "/audits": <AuditsPage />,
    "/claims/:tableName/:searchFilter": <Claims />,
    "/claims": <Claims />,
    "/search/:searchFilter": <LegacySearchRedirect />,
    "/search": <Redirect to="/claims" />,
    "/claims/:searchFilter": <Claims />,
    "/queues": <QueuesPage />,
    "/new-queues": <NewQueuesPage />,
    "/queue/:queueId": <Queue />,
    "/reports": <Reports />,
    "/transfers": <Transfers />,
    "/autodor-rules/:ruleId": <AutodorRulesPage />,
    "/autodor-rules": <AutodorRulesPage />,
    "/home": <Dashboard />,
    "/settings/roles": (
      <AdminSettings
        breadcrumbs={[{ href: "/settings/roles", label: "Roles" }]}
      >
        <RolesPage />
      </AdminSettings>
    ),
    "/settings/users": (
      <AdminSettings
        breadcrumbs={[{ href: "/settings/users/", label: "User Management" }]}
      >
        <Users />
      </AdminSettings>
    ),
    "/settings/sso-identity-providers": (
      <AdminSettings
        breadcrumbs={[
          {
            href: "/settings/sso-identity-providers/",
            label: "SSO Identity Providers",
          },
        ]}
      >
        <SsoIdentityProvidersPage />
      </AdminSettings>
    ),

    // routes available in dev mode only (per env check above)
    "/colors": <Colors />,
    "/playground": <Playground />,

    // catch-all
    "/": <Redirect to={permissions.homeRoute} />,

    // FIXME: (AT) - Assess the following routes / components
    // Notifications are unimplemented today, but coming!
    // '/user/notifications': <Notifications />,
  };

  return (
    <Switch>
      {Object.entries(routes)
        .filter(([route]) => permittedRoutes.includes(route))
        .map(([path, component]) => (
          <Route key={path} path={path}>
            <RouteWrapper boundaryDescription={`Route [${path}]`}>
              {component}
            </RouteWrapper>
          </Route>
        ))}
    </Switch>
  );
};

export { AuthCallbackRoute, Routes };
